import { useForm } from "../../../provider/Form/FormProvider";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import DefenseProcessBaseData from "../AbstractLitigationCase/DefenseProcessBaseData";
import Group from "../../Wizard/Group";
import { translate } from "../../../services/translations/translations";
import CrossIcon from "../../../assets/icons/common/toggleable/IconClose";
import ReminderIcon from "../../../assets/icons/contract/toggleable/IconTime";
import DebtCollectionIcon from "../../../assets/icons/contract/toggleable/IconCompany";
import PaymentOrderIcon from "../../../assets/icons/provision/toggleable/IconPostbox";
import LawsuitIcon from "../../../assets/icons/generic/toggleable/IconCourtDetailed";
import { isMoney, requiredValidator } from "../../../services/validationRules";
import Grid from "@mui/material/Grid";
import React from "react";
import MoneyIcon from "../../../assets/icons/contract/toggleable/IconMoneyEnforce";
import GoodsIcon from "../../../assets/icons/contract/toggleable/IconGoodsEnforce";
import FormLegend from "../Common/FormLegend/FormLegend";
import ValidatorTextField from "../../FormFields/ValidatorTextField";
import NumberFieldConfigured from "../ConfiguredFormElements/NumberFieldConfigured/NumberFieldConfigured";
import MoneyAndGoodsIcon from "../../../assets/icons/contract/toggleable/IconMoneyAndGoodsEnforce";
import wizardStyle from "../wizardStyle";

type EmploymentContractBaseDefendClaimProps = {
  product: AbstractCase;
};

export default function EmploymentContractBaseDefendClaim({ product }: EmploymentContractBaseDefendClaimProps) {
  const { values } = useForm();

  return (
    <>
      <Grid sx={wizardStyle.gridTopSpaced} size={12}>
        <Group
          type="radio"
          name="defenseProcessStatus"
          question={translate(`${product.name}.defenseProcessStatus.question`, values)}
          options={[
            {
              labelText: translate(`${product.name}.defenseProcessStatus.options.none.label`, values),
              labelIcon: <CrossIcon />,
              labelIconActive: <CrossIcon active />,
              value: "none",
            },
            {
              labelText: translate(`${product.name}.defenseProcessStatus.options.reminder.label`, values),
              labelIcon: <ReminderIcon />,
              labelIconActive: <ReminderIcon active />,
              value: "reminder",
            },
            {
              labelText: translate(`${product.name}.defenseProcessStatus.options.debtCollection.label`, values),
              labelIcon: <DebtCollectionIcon />,
              labelIconActive: <DebtCollectionIcon active />,
              value: "debtCollection",
            },
            {
              labelText: translate(`${product.name}.defenseProcessStatus.options.paymentOrder.label`, values),
              labelIcon: <PaymentOrderIcon />,
              labelIconActive: <PaymentOrderIcon active />,
              optionHintText: translate(`${product.name}.defenseProcessStatus.options.paymentOrder.optionHintText`, values),
              value: "paymentOrder",
            },
            {
              labelText: translate(`${product.name}.defenseProcessStatus.options.enforcementOrder.label`, values),
              labelIcon: <PaymentOrderIcon />,
              labelIconActive: <PaymentOrderIcon active />,
              optionHintText: translate(`${product.name}.defenseProcessStatus.options.enforcementOrder.optionHintText`, values),
              value: "enforcementOrder",
            },
            {
              labelText: translate(`${product.name}.defenseProcessStatus.options.lawsuit.label`, values),
              labelIcon: <LawsuitIcon />,
              labelIconActive: <LawsuitIcon active />,
              optionHintText: translate(`${product.name}.defenseProcessStatus.options.lawsuit.optionHintText`, values),
              value: "lawsuit",
            },
          ]}
          validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
        />
      </Grid>
      <DefenseProcessBaseData product={product} />
      <Grid size={12}>
        <Group
          type="radio"
          name="subjectOfDispute"
          question={translate(`${product.name}.subjectOfDispute.question`, values)}
          options={[
            {
              labelText: translate(`${product.name}.subjectOfDispute.options.money.label`, values),
              labelIcon: <MoneyIcon />,
              labelIconActive: <MoneyIcon active />,
              value: "money",
            },
            {
              labelText: translate(`${product.name}.subjectOfDispute.options.goods.label`, values),
              labelIcon: <GoodsIcon />,
              labelIconActive: <GoodsIcon active />,
              value: "goods",
            },
            {
              labelText: translate(`${product.name}.subjectOfDispute.options.moneyAndGoods.label`, values),
              labelIcon: <MoneyAndGoodsIcon sx={{ width: 300 }} />,
              labelIconActive: <MoneyAndGoodsIcon active sx={{ width: 300 }} />,
              value: "moneyAndGoods",
            },
          ]}
          validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
        />
      </Grid>
      {!!values.defenseProcessStatus && values.subjectOfDispute === "money" && (
        <>
          <Grid size={12}>
            <FormLegend>{translate(`${product.name}.claimedAmountClient.question`, values)}</FormLegend>
          </Grid>
          <Grid size={12}>
            <ValidatorTextField
              label={translate(`${product.name}.claimedAmountClient.label`, values)}
              name="claimedAmountClient"
              validators={[
                requiredValidator("Bitte geben Sie Ihre Forderung an."),
                {
                  validator: isMoney,
                  message: "Bitte geben Sie den Wert in Euro und Cent ein - zum Beispiel 136,15 oder 85,00",
                },
              ]}
              helperText={translate(`${product.name}.claimedAmountClient.helperText`, values)}
            />
          </Grid>
          {["reminder", "debtCollection"].includes(values.defenseProcessStatus) && (
            <NumberFieldConfigured caseData={product} fieldPath={"overdueFees"} fieldType={"money"} numberType={"float"} />
          )}
        </>
      )}
    </>
  );
}
